class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget
          .closest('details')
          .hasAttribute('open')
      );
    });

    if (cartDrawerNote.parentElement) {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /*
   * ---------------------------------------------------------
   * UK SHOP FIX
   * Keep the outer cart-drawer "is-empty" class synchronized
   * with the HTML returned by Shopify.
   * ---------------------------------------------------------
   */
  syncEmptyState(html) {
    if (!html) return;

    const drawerDOM = this.getSectionDOM(
      html,
      '#CartDrawer'
    );

    if (!drawerDOM) return;

    const isEmpty = !!drawerDOM.querySelector(
      '.drawer__inner-empty'
    );

    this.classList.toggle('is-empty', isEmpty);

    const drawerItems = this.querySelector(
      'cart-drawer-items'
    );

    if (drawerItems) {
      drawerItems.classList.toggle('is-empty', isEmpty);
    }

    const footer = this.querySelector('.drawer__footer');

    if (footer) {
      footer.classList.toggle('is-empty', isEmpty);
    }
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    const cartDrawerHTML =
      parsedState.sections['cart-drawer'];

    if (!cartDrawerHTML) {
      console.warn(
        'UK SHOP Cart Drawer: cart-drawer section missing'
      );
      return;
    }

    /*
     * Determine empty/non-empty BEFORE replacing the HTML.
     */
    this.syncEmptyState(cartDrawerHTML);

    this.productId = parsedState.id;

    /*
     * Update the complete cart drawer.
     */
    const drawerElement =
      this.querySelector('#CartDrawer');

    const newDrawerElement =
      this.getSectionDOM(
        cartDrawerHTML,
        '#CartDrawer'
      );

    if (drawerElement && newDrawerElement) {
      drawerElement.innerHTML =
        newDrawerElement.innerHTML;
    }

    /*
     * Update cart icon.
     */
    const cartIconHTML =
      parsedState.sections['cart-icon-bubble'];

    if (cartIconHTML) {
      const cartIconElement =
        document.querySelector('#cart-icon-bubble');

      const newCartIconElement =
        this.getSectionDOM(
          cartIconHTML,
          '.shopify-section'
        );

      if (
        cartIconElement &&
        newCartIconElement
      ) {
        cartIconElement.innerHTML =
          newCartIconElement.querySelector(
            '#cart-icon-bubble'
          )?.innerHTML || '';
      }
    }

    /*
     * Sync empty state AGAIN after HTML replacement.
     */
    this.syncEmptyState(cartDrawerHTML);

    /*
     * Rebind overlay because #CartDrawer contents
     * were replaced.
     */
    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this),
        { once: true }
      );
    }

    /*
     * Open after DOM update.
     */
    requestAnimationFrame(() => {
      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return '';

    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsed.querySelector(selector);

    return element
      ? element.innerHTML
      : '';
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return null;

    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    return parsed.querySelector(selector);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}


if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


class CartDrawerItems extends CartItems {

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  /*
   * When the last item is removed, Shopify returns the
   * empty-cart markup inside .drawer__inner.
   *
   * The outer <cart-drawer> element also needs the
   * "is-empty" class restored.
   */
  renderContents(parsedState) {
    super.renderContents(parsedState);

    const cartDrawer =
      document.querySelector('cart-drawer');

    if (!cartDrawer) return;

    const drawerInner =
      cartDrawer.querySelector('.drawer__inner');

    if (!drawerInner) return;

    const isEmpty =
      !!drawerInner.querySelector(
        '.drawer__inner-empty'
      );

    cartDrawer.classList.toggle(
      'is-empty',
      isEmpty
    );

    this.classList.toggle(
      'is-empty',
      isEmpty
    );

    const footer =
      cartDrawer.querySelector(
        '.drawer__footer'
      );

    if (footer) {
      footer.classList.toggle(
        'is-empty',
        isEmpty
      );
    }
  }
}


if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}